import { Button, Dialog, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { ReservedPointsCard } from '@/components/shared/Booking/ReservedPointsCard';
import {
  LockedSubwalletCards,
} from '@/components/shared/SubwalletBalance/LockedSubwalletCards';
import { TotalPointsAvailableCard } from "@/components/shared/Subwallet/TotalPointsAvailableCard";
import { SubwalletList } from '@/components/shared/SubwalletBalance/SubwalletList';
import { QueryKeys } from '@/constants/QueryKeys';
import { AboutAccountRolesBanner } from "@/pages/CustomerDashboard/LinkedAccounts";
import { PointsExpirationBanner } from '@/pages/CustomerDashboard/PointsExpirationBanner';
import { OrderTransactionsApi } from "@/utilities/api/OrderTransactionsApi";
import { ApiRetailer, ApiSubwalletBalance, ApiUser } from '@api/interfaces';
import { Stack, useTheme } from '@mui/material';
import { CIBPageName } from '@shared/enums';
import { calculateLockedPoints, getAccountName } from '@shared/utilities';
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

type SubwalletListDialogProps = {
  balances?: ApiSubwalletBalance[],
  customer: ApiUser,
  linkedAccounts?: ApiUser[],
  retailer?: ApiRetailer,
  show: boolean,
  setShow: Dispatch<SetStateAction<boolean>>,
}

export const SubwalletListDialog = ({
  balances,
  customer,
  linkedAccounts,
  retailer,
  show,
  setShow,
}: SubwalletListDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const brandColor = retailer?.brandColor ?? theme.palette.primary.main;

  const { data: latestProcessedOrderDate } = useQuery(
    [QueryKeys.GET_LATEST_PROCESSED_ORDER_DATE, retailer?.id],
    () => OrderTransactionsApi.getLatestProcessedOrderDate({
      retailerId: retailer?.id,
    }),
    { enabled: !!retailer?.id },
  );

  const { data: bookingsData } = useQuery(
    [QueryKeys.LIST_BOOKINGS, customer.id],
    () => OrderTransactionsApi.listBookingsByFarmerId(customer.id),
    { enabled: !!customer.id },
  );

  return (
    <>
      <Dialog
        acceptButton={(props) => (
          <Button
            {...props}
            onClick={() => setShow(false)}
            testID="subwallet-balance-dialog-close-button"
          >
            {t($ => $.common.actions.close)}
          </Button>
        )}
        onClose={() => setShow(false)}
        open={show}
        px={0}
        showCloseButton={false}
        sx={{
          backgroundColor: '#F7F7F7',
        }}
        testID="subwallet-balance-dialog"
        title={(
          <Stack alignItems="center" direction="row" justifyContent="space-between">
            <Text category="headline-small" sx={{ overflowWrap: 'break-word' }}>
              {t($ => $.customers.details.wallet)}
            </Text>
            {!!latestProcessedOrderDate && (
              <Text
                category="body-small"
                flexShrink={0}
                sx={{ color: theme.palette.text.secondary }}
              >
                {t($ => $.common.updated, { date: latestProcessedOrderDate })}
              </Text>
            )}
          </Stack>
        )}
      >
        <PointsExpirationBanner customer={customer} sx={{ marginBottom: '16px' }}/>
        <Stack px="24px">
          <Stack direction="row">
            <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
              {t($ => $.customers.progress.account)}
            </Text>
            <HSpacer size="2" />
            <Text
              category="body-medium"
              sx={{ color: theme.palette.text.secondary }}
            >
              {getAccountName(customer)}
            </Text>
          </Stack>
          <VSpacer size="3" />
          {!!customer.accountGroupRole && !!linkedAccounts?.length && (
            <>
              <AboutAccountRolesBanner
                customerRole={customer.accountGroupRole}
                linkedAccounts={linkedAccounts}
                pageName={CIBPageName.RP_Wallet}
                showMobileVersion={true}
              />
              <VSpacer size="6" />
            </>
          )}
          <TotalPointsAvailableCard
            accountGroupRole={customer.accountGroupRole}
            balances={balances ?? []}
            brandColor={brandColor}
            retailer={retailer}
          />
          <VSpacer size="6" />
          {!!retailer?.hasBookings && (
            <ReservedPointsCard
              balances={balances ?? []}
              bookings={bookingsData ?? []}
              customer={customer}
              hideBorder
              linkedAccounts={linkedAccounts}
              sx={{ pb: '20px' }}
            />
          )}
          <Text category="title-medium">
            {t($ => $.customers.viewRewards.availablePoints)}
          </Text>
          <VSpacer size="4" />
          <SubwalletList customer={customer} hideBorder />
          <VSpacer size="2" />
          {!!calculateLockedPoints(balances ?? []) && (
            <>
              <VSpacer size="4" />
              <Text category="title-medium">
                {t($ => $.wallet.locked.title)}
              </Text>
              <VSpacer size="4" />
              <LockedSubwalletCards
                brandColor={brandColor}
                customer={customer}
                hideBorder
              />
              <VSpacer size="2" />
            </>
          )}
        </Stack>
      </Dialog>
    </>
  );
};
